import { useState, useEffect, useCallback, useRef } from "react";
import { onDocumentDragOver, isIeOrEdge } from "../utils";
import { useFileProcessing } from "./useFileProcessing";

export const useDropzone = props => {
  const {
    inputProps,
    multiple,
    preventDropOnDocument,
    disablePreview,
    disableClick,
    onDropAccepted,
    onDropRejected,
    maxSize,
    minSize,
    getDataTransferItems,
    onClick,
    onDrop,
    onDragEnter: propsOnDragEnter,
    onDragLeave: propsOnDragLeave,
  } = props;

  const [draggedFiles, setDraggedFiles] = useState([]);
  const [acceptedFiles, setAcceptedFiles] = useState([]);
  const [rejectedFiles, setRejectedFiles] = useState([]);
  const [isDragActive, setIsDragActive] = useState(false);
  const { processFiles, isProcessing, error } = useFileProcessing();

  const node = useRef(null);
  const fileInputEl = useRef(null);
  const dragTargets = useRef([]);
  const isFileDialogActive = useRef(false);

  useEffect(() => {
    if (preventDropOnDocument) {
      document.addEventListener("dragover", onDocumentDragOver, false);
      document.addEventListener("drop", onDocumentDrop, false);
    }

    if (fileInputEl.current) {
      fileInputEl.current.addEventListener("click", onInputElementClick, false);
    }

    window.addEventListener("focus", onFileDialogCancel, false);

    return () => {
      if (preventDropOnDocument) {
        document.removeEventListener("dragover", onDocumentDragOver);
        document.removeEventListener("drop", onDocumentDrop);
      }
      if (fileInputEl.current) {
        fileInputEl.current.removeEventListener("click", onInputElementClick, false);
      }
      window.removeEventListener("focus", onFileDialogCancel, false);
    };
  }, [preventDropOnDocument]);

  const onDocumentDrop = useCallback(evt => {
    if (node.current && node.current.contains(evt.target)) {
      return;
    }
    evt.preventDefault();
    dragTargets.current = [];
  }, []);

  const onDragStart = useCallback(
    evt => {
      if (onDragStart) {
        onDragStart.call(null, evt);
      }
    },
    [onDragStart]
  );

  const onDragEnter = useCallback(
    evt => {
      evt.preventDefault();

      if (dragTargets.current.indexOf(evt.target) === -1) {
        dragTargets.current.push(evt.target);
      }

      Promise.resolve(getDataTransferItems(evt)).then(files => {
        setIsDragActive(true);
        setDraggedFiles(files);
      });

      if (propsOnDragEnter) {
        propsOnDragEnter.call(null, evt);
      }
    },
    [getDataTransferItems, propsOnDragEnter]
  );

  const onDragOver = useCallback(
    evt => {
      evt.preventDefault();
      evt.stopPropagation();
      try {
        evt.dataTransfer.dropEffect = isFileDialogActive.current ? "none" : "copy";
      } catch (err) {
        // continue regardless of error
      }
      if (onDragOver) {
        onDragOver.call(null, evt);
      }
      return false;
    },
    [onDragOver]
  );

  const onDragLeave = useCallback(
    evt => {
      evt.preventDefault();

      dragTargets.current = dragTargets.current.filter(
        el => el !== evt.target && node.current.contains(el)
      );
      if (dragTargets.current.length > 0) {
        return;
      }

      setIsDragActive(false);
      setDraggedFiles([]);

      if (propsOnDragLeave) {
        propsOnDragLeave.call(null, evt);
      }
    },
    [propsOnDragLeave]
  );

  const onDropHandler = useCallback(
    evt => {
      evt.preventDefault();
      dragTargets.current = [];
      isFileDialogActive.current = false;
      setIsDragActive(false);
      setDraggedFiles([]);

      Promise.resolve(getDataTransferItems(evt))
        .then(fileList => processFiles(fileList, { disablePreview, maxSize, minSize }))
        .then(({ acceptedFiles, rejectedFiles }) => {
          if (!multiple) {
            rejectedFiles.push(...acceptedFiles.splice(1));
          }

          if (onDrop) {
            onDrop(acceptedFiles, rejectedFiles, evt);
          }

          if (rejectedFiles.length > 0 && onDropRejected) {
            onDropRejected(rejectedFiles, evt);
          }

          if (acceptedFiles.length > 0 && onDropAccepted) {
            onDropAccepted(acceptedFiles, evt);
          }

          setAcceptedFiles(acceptedFiles);
          setRejectedFiles(rejectedFiles);
        });
    },
    [
      multiple,
      onDrop,
      onDropRejected,
      onDropAccepted,
      maxSize,
      minSize,
      disablePreview,
      getDataTransferItems,
    ]
  );

  const onClickHandler = useCallback(
    evt => {
      if (!disableClick) {
        evt.stopPropagation();

        if (onClick) {
          onClick.call(null, evt);
        }

        if (isIeOrEdge()) {
          setTimeout(openFileDialog, 0);
        } else {
          openFileDialog();
        }
      }
    },
    [disableClick, onClick]
  );

  const onInputElementClick = useCallback(
    evt => {
      evt.stopPropagation();
      if (inputProps && inputProps.onClick) {
        inputProps.onClick();
      }
    },
    [inputProps]
  );

  const onFileDialogCancel = useCallback(() => {
    if (isFileDialogActive.current) {
      setTimeout(() => {
        if (fileInputEl.current) {
          const { files } = fileInputEl.current;

          if (!files.length) {
            isFileDialogActive.current = false;
          }
        }

        if (typeof onFileDialogCancel === "function") {
          onFileDialogCancel();
        }
      }, 300);
    }
  }, [onFileDialogCancel]);

  const openFileDialog = useCallback(() => {
    isFileDialogActive.current = true;
    fileInputEl.current.value = null;
    fileInputEl.current.click();
  }, []);

  return {
    node,
    fileInputEl,
    draggedFiles,
    acceptedFiles,
    rejectedFiles,
    isDragActive,
    isProcessing,
    error,
    onDragStart,
    onDragEnter,
    onDragOver,
    onDragLeave,
    onDropHandler,
    onClickHandler,
  };
};



// WEBPACK FOOTER //
// ./components/common/file-uploader/hooks/useDropzone.js